import { apiService } from '../../../components/api/apiService';
import { logDataModels } from './dataModelsLogger';
import type { DataModel, DataModelsCatalog, DataSource, DataSourceKind, ModelTable, Space, SpaceIconKey } from './dataModelsTypes';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/data-model-workspaces';

export type WorkspaceTreeRecord = Space & {
  sources?: DataSource[];
  models?: Array<DataModel & { tables?: ModelTable[] }>;
};

function serviceErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as {
    response?: { data?: { error?: { message?: string }; message?: string } };
    message?: string;
  };
  return axiosErr.response?.data?.error?.message || axiosErr.response?.data?.message || axiosErr.message || fallback;
}

function asList(data: unknown): WorkspaceTreeRecord[] {
  const record = data && typeof data === 'object' ? (data as { workspaces?: unknown }) : {};
  return Array.isArray(record.workspaces) ? (record.workspaces as WorkspaceTreeRecord[]) : [];
}

export function emptyCatalog(): DataModelsCatalog {
  return {
    spaces: [],
    models: [],
    tables: [],
    sources: [],
    mcpTools: [],
    members: [],
    policies: [],
    activity: [],
  };
}

export function catalogFromWorkspaces(rows: WorkspaceTreeRecord[]): DataModelsCatalog {
  const catalog = emptyCatalog();
  const sourceSeen = new Set<string>();
  rows.forEach((row) => {
    const spaceId = String(row.id || '');
    if (!spaceId) return;
    catalog.spaces.push({
      id: spaceId,
      name: row.name,
      description: row.description || '',
      icon: (row.icon || 'customer') as SpaceIconKey,
      status: row.status === 'archived' ? 'archived' : 'active',
      owner: row.owner || '',
      createdAt: row.createdAt || '',
      updatedAt: row.updatedAt || '',
      domain: row.domain || row.name,
      tags: row.tags || [],
      headline: row.headline || '',
      sourceIds: Array.from(new Set([...(row.sourceIds || []), ...((row.sources || []).map((source) => source?.id).filter(Boolean) as string[])])),
    });
    (row.sources || []).forEach((source) => {
      if (!source?.id || sourceSeen.has(source.id)) return;
      sourceSeen.add(source.id);
      catalog.sources.push({
        id: source.id,
        name: source.name,
        kind: (source.kind || 'postgresql') as DataSourceKind,
        environment: source.environment || '',
        connectionType: source.connectionType,
        connectorKey: source.connectorKey,
      });
    });
    (row.models || []).forEach((model) => {
      const modelId = String(model.id || '');
      if (!modelId) return;
      catalog.models.push({
        id: modelId,
        spaceId,
        name: model.name,
        description: model.description || '',
        starred: Boolean(model.starred),
        owner: model.owner || row.owner || '',
        updatedAt: model.updatedAt || row.updatedAt || '',
        version: model.version || 'v0.1.0',
        status: model.status === 'published' ? 'published' : 'draft',
        tags: model.tags || [],
      });
      (model.tables || []).forEach((table) => {
        if (!table?.id || !table.name) return;
        catalog.tables.push({
          id: table.id,
          modelId,
          name: table.name,
          description: table.description || '',
          rows: table.rows || 0,
          columns: table.columns || 0,
          sourceId: table.sourceId,
          path: table.path,
          assetKind: table.assetKind,
          mimeType: table.mimeType,
          updatedAt: table.updatedAt || model.updatedAt || '',
          status: table.status === 'published' ? 'published' : 'draft',
        });
      });
    });
  });
  return catalog;
}

export function mergeWorkspace(catalog: DataModelsCatalog, row: WorkspaceTreeRecord): DataModelsCatalog {
  const piece = catalogFromWorkspaces([row]);
  const previousModelIds = new Set(catalog.models.filter((model) => model.spaceId === row.id).map((model) => model.id));
  return {
    ...catalog,
    spaces: [piece.spaces[0], ...catalog.spaces.filter((space) => space.id !== row.id)].filter(Boolean),
    models: [...catalog.models.filter((model) => model.spaceId !== row.id), ...piece.models],
    tables: [...catalog.tables.filter((table) => !previousModelIds.has(table.modelId)), ...piece.tables],
    sources: [...catalog.sources.filter((source) => !piece.sources.some((next) => next.id === source.id)), ...piece.sources],
  };
}

export async function listWorkspaces(): Promise<WorkspaceTreeRecord[]> {
  logDataModels('app', 'workspaces_load');
  try {
    const data = await apiService.get(BASE, {}, CONNECTION);
    const rows = asList(data);
    logDataModels('app', 'workspaces_load_ok', { count: rows.length });
    return rows;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to load WorkSpaces');
    logDataModels('error', 'workspaces_load_failed', { message });
    throw new Error(message);
  }
}

export async function createWorkspace(body: Record<string, unknown>): Promise<WorkspaceTreeRecord> {
  logDataModels('app', 'workspace_create', { name: body.name });
  try {
    const data = (await apiService.post(BASE, body, CONNECTION)) as WorkspaceTreeRecord;
    logDataModels('app', 'workspace_create_ok', { id: data?.id, name: data?.name });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to create WorkSpace');
    logDataModels('error', 'workspace_create_failed', { message });
    throw new Error(message);
  }
}

export async function updateWorkspace(id: string, body: Record<string, unknown>): Promise<WorkspaceTreeRecord> {
  logDataModels('app', 'workspace_update', { id });
  try {
    const data = (await apiService.put(`${BASE}/${encodeURIComponent(id)}`, body, CONNECTION)) as WorkspaceTreeRecord;
    logDataModels('app', 'workspace_update_ok', { id });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to update WorkSpace');
    logDataModels('error', 'workspace_update_failed', { id, message });
    throw new Error(message);
  }
}

export function dropWorkspace(catalog: DataModelsCatalog, spaceId: string): DataModelsCatalog {
  const modelIds = new Set(catalog.models.filter((model) => model.spaceId === spaceId).map((model) => model.id));
  return {
    ...catalog,
    spaces: catalog.spaces.filter((space) => space.id !== spaceId),
    models: catalog.models.filter((model) => model.spaceId !== spaceId),
    tables: catalog.tables.filter((table) => !modelIds.has(table.modelId)),
    members: catalog.members.filter((member) => member.spaceId !== spaceId),
    activity: catalog.activity.filter((event) => event.spaceId !== spaceId),
    mcpTools: catalog.mcpTools.filter((tool) => !modelIds.has(tool.modelId)),
    policies: catalog.policies.filter((policy) => policy.spaceId !== spaceId),
  };
}

export async function deleteWorkspace(workspaceId: string): Promise<void> {
  logDataModels('app', 'workspace_delete', { workspaceId });
  try {
    await apiService.delete(`${BASE}/${encodeURIComponent(workspaceId)}`, CONNECTION);
    logDataModels('app', 'workspace_delete_ok', { workspaceId });
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to delete WorkSpace');
    logDataModels('error', 'workspace_delete_failed', { workspaceId, message });
    throw new Error(message);
  }
}

export async function attachWorkspaceConnections(workspaceId: string, sources: DataSource[]): Promise<WorkspaceTreeRecord> {
  logDataModels('app', 'workspace_connections_attach', { workspaceId, count: sources.length });
  try {
    const data = (await apiService.post(
      `${BASE}/${encodeURIComponent(workspaceId)}/connections`,
      { sources },
      CONNECTION,
    )) as WorkspaceTreeRecord;
    logDataModels('app', 'workspace_connections_attach_ok', { workspaceId, count: sources.length });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to attach connections');
    logDataModels('error', 'workspace_connections_attach_failed', { workspaceId, message });
    throw new Error(message);
  }
}

export async function detachWorkspaceConnection(workspaceId: string, sourceId: string): Promise<WorkspaceTreeRecord> {
  logDataModels('app', 'workspace_connection_detach', { workspaceId, sourceId });
  try {
    const data = (await apiService.delete(
      `${BASE}/${encodeURIComponent(workspaceId)}/connections/${encodeURIComponent(sourceId)}`,
      CONNECTION,
    )) as WorkspaceTreeRecord;
    logDataModels('app', 'workspace_connection_detach_ok', { workspaceId, sourceId });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to remove connection');
    logDataModels('error', 'workspace_connection_detach_failed', { workspaceId, sourceId, message });
    throw new Error(message);
  }
}

export async function appendWorkspaceAssets(
  workspaceId: string,
  modelId: string,
  body: { tables: Array<Record<string, unknown>>; sources?: DataSource[] },
): Promise<WorkspaceTreeRecord> {
  logDataModels('app', 'workspace_assets_append', { workspaceId, modelId, count: body.tables.length });
  try {
    const data = (await apiService.post(
      `${BASE}/${encodeURIComponent(workspaceId)}/models/${encodeURIComponent(modelId)}/assets`,
      body,
      CONNECTION,
    )) as WorkspaceTreeRecord;
    logDataModels('app', 'workspace_assets_append_ok', { workspaceId, modelId, count: body.tables.length });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to add assets');
    logDataModels('error', 'workspace_assets_append_failed', { workspaceId, modelId, message });
    throw new Error(message);
  }
}

export async function removeWorkspaceAsset(workspaceId: string, modelId: string, tableId: string): Promise<WorkspaceTreeRecord> {
  logDataModels('app', 'workspace_asset_remove', { workspaceId, modelId, tableId });
  try {
    const data = (await apiService.delete(
      `${BASE}/${encodeURIComponent(workspaceId)}/models/${encodeURIComponent(modelId)}/assets/${encodeURIComponent(tableId)}`,
      CONNECTION,
    )) as WorkspaceTreeRecord;
    logDataModels('app', 'workspace_asset_remove_ok', { workspaceId, modelId, tableId });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to remove asset');
    logDataModels('error', 'workspace_asset_remove_failed', { workspaceId, modelId, tableId, message });
    throw new Error(message);
  }
}

export async function deleteWorkspaceModel(workspaceId: string, modelId: string): Promise<WorkspaceTreeRecord> {
  logDataModels('app', 'workspace_model_delete', { workspaceId, modelId });
  try {
    const data = (await apiService.delete(
      `${BASE}/${encodeURIComponent(workspaceId)}/models/${encodeURIComponent(modelId)}`,
      CONNECTION,
    )) as WorkspaceTreeRecord;
    logDataModels('app', 'workspace_model_delete_ok', { workspaceId, modelId });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to delete data model');
    logDataModels('error', 'workspace_model_delete_failed', { workspaceId, modelId, message });
    throw new Error(message);
  }
}

export async function updateWorkspaceModel(
  workspaceId: string,
  modelId: string,
  body: Record<string, unknown>,
): Promise<WorkspaceTreeRecord> {
  logDataModels('app', 'workspace_model_update', { workspaceId, modelId, name: body.name });
  try {
    const data = (await apiService.put(
      `${BASE}/${encodeURIComponent(workspaceId)}/models/${encodeURIComponent(modelId)}`,
      body,
      CONNECTION,
    )) as WorkspaceTreeRecord;
    logDataModels('app', 'workspace_model_update_ok', { workspaceId, modelId });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to update data model');
    logDataModels('error', 'workspace_model_update_failed', { workspaceId, modelId, message });
    throw new Error(message);
  }
}

export async function createWorkspaceModel(workspaceId: string, body: Record<string, unknown>): Promise<WorkspaceTreeRecord> {
  logDataModels('app', 'workspace_model_create', { workspaceId, name: body.name });
  try {
    const data = (await apiService.post(`${BASE}/${encodeURIComponent(workspaceId)}/models`, body, CONNECTION)) as WorkspaceTreeRecord;
    logDataModels('app', 'workspace_model_create_ok', { workspaceId, id: data?.id });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to create data model');
    logDataModels('error', 'workspace_model_create_failed', { workspaceId, message });
    throw new Error(message);
  }
}
